/**
 * SAP UI5 Fiori 3.0 Design System
 * Based on official SAP design guidelines for enterprise applications
 * https://experience.sap.com/fiori-design-web/
 */

// SAP Official Color Palette (Fiori 3.0)
export const SAP_COLORS = {
  // Primary Brand Colors
  sapBlue: '#0070F2',           // Primary actions, links, selected states
  sapBlueDark: '#002C5F',       // Headers, emphasis text (alias for sapDarkBlue)
  sapDarkBlue: '#002C5F',       // Headers, emphasis text
  sapLightBlue: '#D1E8FF',      // Background hover, light selections
  sapPaleBlue: '#F5FAFF',       // Background areas, panels
  
  // Semantic Colors
  sapGreen: '#30914C',          // Success states, positive actions
  sapLightGreen: '#E5F3E9',     // Success backgrounds
  sapRed: '#CC1919',            // Error states, critical actions
  sapLightRed: '#FEECEE',       // Error backgrounds
  sapOrange: '#E76500',         // Warning states, attention
  sapLightOrange: '#FEF2E6',    // Warning backgrounds
  sapYellow: '#FFAB1D',         // Information states, highlights
  sapLightYellow: '#FFF8E6',    // Information backgrounds
  
  // Neutral Palette
  sapGray1: '#F7F7F7',          // Page background
  sapGray2: '#EDEDED',          // Card background, borders
  sapGray3: '#D9D9D9',          // Dividers, inactive borders
  sapGray4: '#BFBFBF',          // Disabled text, placeholders
  sapGray5: '#89919A',          // Secondary text, labels
  sapGray6: '#5A6270',          // Body text
  sapGray7: '#32363A',          // Primary text, headers
  sapBlack: '#000000',          // High contrast text
  sapWhite: '#FFFFFF',          // Pure white backgrounds
  
  // Interactive States
  hover: '#E6F3FF',             // Hover backgrounds
  active: '#CCE5FF',            // Active/pressed states
  focus: '#0070F2',             // Focus indicators
  disabled: '#F2F2F2',         // Disabled backgrounds
  
  // Chart Colors (for dashboards)
  chart1: '#5899DA',
  chart2: '#E8743B',
  chart3: '#19A979',
  chart4: '#ED4A7B',
  chart5: '#945ECF',
  chart6: '#13A4B4',
  chart7: '#525DF4',
  chart8: '#BF399E',
  
  // Enhanced Enterprise Colors
  enterprise: {
    primary: '#0070F2',
    secondary: '#718096',
    accent: '#00C4B4',
    neutral: '#F7FAFC',
    surface: '#FFFFFF',
    background: '#F8F9FA',
    border: '#E2E8F0',
    text: {
      primary: '#1A202C',
      secondary: '#4A5568',
      tertiary: '#718096',
      disabled: '#A0AEC0',
      inverse: '#FFFFFF',
    },
    status: {
      success: '#38A169',
      warning: '#D69E2E',
      error: '#E53E3E',
      info: '#3182CE',
    },
  },
} as const;

// SAP Typography Scale
export const SAP_TYPOGRAPHY = {
  // Font Family
  fontFamily: {
    primary: '"SAP-72", "72", "72full", Arial, Helvetica, sans-serif',
    mono: '"SAP-72-Mono", "Courier New", Courier, monospace',
    enterprise: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
  },
  
  // Font Sizes (rem based)
  fontSize: {
    xs: '0.75rem',      // 12px - Small labels, captions
    sm: '0.875rem',     // 14px - Body text, form inputs
    md: '1rem',         // 16px - Default body
    lg: '1.125rem',     // 18px - Large body text
    xl: '1.25rem',      // 20px - Small headings
    '2xl': '1.5rem',    // 24px - Section headings
    '3xl': '1.875rem',  // 30px - Page headings
    '4xl': '2.25rem',   // 36px - Large page titles
    '5xl': '3rem',      // 48px - Hero text
    '6xl': '3.75rem',   // 60px - Display text
  },
  
  // Font Weights
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  
  // Line Heights
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
    loose: 2,
  },
  
  // Letter Spacing
  letterSpacing: {
    tight: '-0.025em',
    normal: '0em',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
} as const;

// SAP Spacing System (8px base grid)
export const SAP_SPACING = {
  // Base unit: 0.5rem = 8px
  xs: '0.25rem',    // 4px - Tight spacing
  sm: '0.5rem',     // 8px - Base unit
  md: '1rem',       // 16px - Standard spacing
  lg: '1.5rem',     // 24px - Section spacing
  xl: '2rem',       // 32px - Large spacing
  '2xl': '3rem',    // 48px - Extra large spacing
  '3xl': '4rem',    // 64px - Page sections
  '4xl': '6rem',    // 96px - Hero sections
  '5xl': '8rem',    // 128px - Extra large sections
  '6xl': '12rem',   // 192px - Maximum spacing
  
  // Component specific
  component: {
    padding: '1rem',          // Standard padding
    margin: '1rem',           // Standard margin
    borderRadius: '0.375rem', // 6px - Standard border radius
    borderWidth: '1px',       // Standard border
    gap: '0.5rem',            // Standard gap
  },
  
  // Layout specific
  layout: {
    section: '4rem',          // Between major sections
    subsection: '3rem',       // Between subsections
    content: '2rem',          // Between content blocks
    element: '1.5rem',        // Between elements
    item: '1rem',             // Between list items
    inline: '0.5rem',         // Between inline elements
  },
} as const;

// SAP Shadows (Elevation system)
export const SAP_SHADOWS = {
  none: 'none',
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  card: '0 2px 8px 0 rgba(0, 0, 0, 0.1)',
  modal: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  
  // Enhanced enterprise shadows
  enterprise: {
    subtle: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    large: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xlarge: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    xxlarge: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  },
} as const;

// SAP Breakpoints (responsive design)
export const SAP_BREAKPOINTS = {
  xs: '0px',        // Mobile portrait
  sm: '600px',      // Mobile landscape
  md: '960px',      // Tablet
  lg: '1280px',     // Desktop
  xl: '1920px',     // Large desktop
  '2xl': '2560px',  // Ultra-wide desktop
} as const;

// SAP Z-Index scale
export const SAP_Z_INDEX = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  overlay: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  toast: 1080,
  
  // Enhanced enterprise z-index
  enterprise: {
    base: 1,
    card: 10,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    overlay: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
    toast: 1080,
    max: 9999,
  },
} as const;

// SAP Animation/Transition values
export const SAP_TRANSITIONS = {
  fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
  normal: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
  
  // Enhanced enterprise transitions
  enterprise: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '500ms cubic-bezier(0.4, 0, 0.2, 1)',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;

// Component size variants
export const SAP_SIZES = {
  button: {
    xs: { height: '1.75rem', padding: '0 0.5rem', fontSize: SAP_TYPOGRAPHY.fontSize.xs },
    sm: { height: '2rem', padding: '0 0.75rem', fontSize: SAP_TYPOGRAPHY.fontSize.sm },
    md: { height: '2.5rem', padding: '0 1rem', fontSize: SAP_TYPOGRAPHY.fontSize.md },
    lg: { height: '3rem', padding: '0 1.5rem', fontSize: SAP_TYPOGRAPHY.fontSize.lg },
    xl: { height: '3.5rem', padding: '0 2rem', fontSize: SAP_TYPOGRAPHY.fontSize.xl },
  },
  input: {
    xs: { height: '1.75rem', fontSize: SAP_TYPOGRAPHY.fontSize.xs },
    sm: { height: '2rem', fontSize: SAP_TYPOGRAPHY.fontSize.sm },
    md: { height: '2.5rem', fontSize: SAP_TYPOGRAPHY.fontSize.md },
    lg: { height: '3rem', fontSize: SAP_TYPOGRAPHY.fontSize.lg },
    xl: { height: '3.5rem', fontSize: SAP_TYPOGRAPHY.fontSize.xl },
  },
  icon: {
    xs: '0.75rem',   // 12px
    sm: '1rem',      // 16px
    md: '1.25rem',   // 20px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '2.5rem', // 40px
  },
  
  // Enhanced enterprise sizes
  enterprise: {
    avatar: {
      xs: '1.5rem',   // 24px
      sm: '2rem',     // 32px
      md: '2.5rem',   // 40px
      lg: '3rem',     // 48px
      xl: '4rem',     // 64px
    },
    card: {
      padding: {
        xs: '1rem',
        sm: '1.5rem',
        md: '2rem',
        lg: '2.5rem',
        xl: '3rem',
      },
      borderRadius: {
        xs: '0.375rem', // 6px
        sm: '0.5rem',   // 8px
        md: '0.75rem',  // 12px
        lg: '1rem',     // 16px
        xl: '1.5rem',   // 24px
      },
    },
  },
} as const;

// Export all design tokens
export const sapDesignSystem = {
  colors: SAP_COLORS,
  typography: SAP_TYPOGRAPHY,
  spacing: SAP_SPACING,
  shadows: SAP_SHADOWS,
  breakpoints: SAP_BREAKPOINTS,
  zIndex: SAP_Z_INDEX,
  transitions: SAP_TRANSITIONS,
  sizes: SAP_SIZES,
} as const;

export type SAPDesignSystem = typeof sapDesignSystem;